<script lang="ts">
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import { calculateCompletedXSS, formatXSSDetails, XSSDeviationCategory, xssDeviationToCategory, type XSSDetails, type XSSDeviationDetails } from '@/Util/utils';
  import { ActivityTag } from '@/Common/ActivityTag';
  import { TagWrapper } from '@/Common';
  import XSSDisplay from '../../Common/XSSDisplay.svelte';
  import TodayActivityCardActions from './TodayActivityCardActions.svelte';
  import { cn } from '@/lib/utils';
  import { Separator } from '@/lib/components/ui/separator';
  import { Button, buttonVariants } from '@/lib/components/ui/button';
  import type { TodaysActivities } from '@/Common/constants';
  import * as Popover from "$lib/components/ui/popover";
  import { _ } from 'svelte-i18n';
  import TodayActivity from './TodayActivity.svelte';
  import { getContext } from 'svelte';
  import type { MyFitnessStores } from '../stores';
  import { getForecastActivityState } from '@/Common/forecastActivityState';
  import ForecastActivityExplanation from '@/Common/ForecastActivityExplanation.svelte';

  export let activity: Exercise;
  export let todaysActivities: TodaysActivities;
  export let selectable: boolean = false;
  export let onSelectedChange: (selected: boolean) => void = () => {};
  // This slightly changes how the title, activity time, xss, distance, and duration are laid out
  // to put the activity time in the top right of the card (better suited for timeline view)
  export let useTimelineLayout: boolean = false;
  export let activityImageCarouselNumber: Record<string, number>;
  export let conflictNotes: string[] = [];

  export let selected: boolean = false;

  $: isHidden = Activity.isHidden(activity);
  $: isDuplicate = Activity.isDuplicate(activity);
  $: isPlaceholder = Activity.isPlaceholder(activity);
  $: isPinned = Activity.isPinned(activity);
  $: placeholderName = isPinned ? activity.name : Activity.getPlaceholderName(activity);

  $: xssDetails = activity.placeholder_xss_details && !isPinned ? {
    xss: activity.placeholder_xss_details?.xss ?? 0,
    xlss: activity.placeholder_xss_details?.xlss ?? 0,
    xhss: activity.placeholder_xss_details?.xhss ?? 0,
    xpss: activity.placeholder_xss_details?.xpss ?? 0,
  } : {
    xss: activity.xss ?? 0,
    xlss: activity.xlss ?? 0,
    xhss: activity.xhss ?? 0,
    xpss: activity.xpss ?? 0,
  };
  $: formattedXSSDetails = formatXSSDetails(xssDetails);

  $: matchedActivities = todaysActivities.activities.filter(a => Activity.isMatchedAtoB(a, activity));
  
  $: completedXSS = calculateCompletedXSS(activity, matchedActivities);
  $: formattedCompletedXSS = completedXSS ? formatXSSDetails(completedXSS) : null;

  let xssDeviationCategory: XSSDetails<XSSDeviationDetails> | null = null;
  $: {
    if (matchedActivities && matchedActivities.length > 0 && xssDetails && completedXSS) {
      xssDeviationCategory = xssDeviationToCategory(xssDetails, completedXSS);
    } else {
      formattedCompletedXSS = null;
    }
  }

  let isLoading = false;

  $: assignedTrainingDescriptor = Activity.getWorkoutPath(activity) ? 'Workout' :
    activity.venue == 'outdoor' ? 'Outdoor Activity' :
    activity.venue == 'virtual' ? 'Virtual Activity' :
    '';

  const myFitness = getContext<MyFitnessStores>('myFitness');
  const { trainingPlan, trainingAdvice } = myFitness;
  // The date of the last breakthrough is not loaded here; the Training Explanation does not use it.
  $: forecastInfo = getForecastActivityState(activity, $trainingPlan, $trainingAdvice?.signature, null);

  const activityTag = ActivityTag('small');
  $: activityTags = [
    activityTag.pinned(activity),
    activityTag.forecast(activity),
  ].filter((tag) => tag);

</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div class='flex flex-col flex-1 gap-2 h-max'>
  <div class="flex flex-wrap items-center justify-between gap-2">
    <div class="flex flex-wrap items-center gap-3">
      {#if selectable}
        <Button variant={selected ? 'default' : 'outline'} size='sm' class='p-0 w-5 h-5 flex items-center justify-center' on:click={(e) => { e.stopPropagation(); selected = !selected; onSelectedChange(selected); }}>
          {#if selected}
            <i class='fa-solid fa-check text-sm' />
          {/if}
        </Button>
      {/if}
      <div class='flex flex-row items-center gap-1'>
        {#if isLoading}
          <i class="fa-solid fa-spinner fa-spin text-gray-400" />
        {/if}
        {#if xssDeviationCategory && xssDeviationCategory.xss.suitability === XSSDeviationCategory.Good}
          <i class='fa-solid fa-lg text-center text-[var(--color-xss-deviation-good)] fa-badge-check' />
        {/if}
        {#if conflictNotes.length > 0}
          <Popover.Root portal="#shadcn-ui-dialog-target">
            <Popover.Trigger class={cn(buttonVariants({ variant: "ghost", size: 'icon' }), 'rounded-full w-7 h-7 text-[#FF00FF]')}>
              <i class="fa-solid fa-triangle-exclamation fa-lg"/>
            </Popover.Trigger>
            <Popover.Content side='top' class="w-[300px] min-w-0 text-gray-600">
              <ul class="list-disc pl-5 space-y-1">
                {#each conflictNotes as note}
                  {#if note}
                  <li class='text-sm'>
                    {$_('forecast.constraints.'+note)}
                  </li>
                  {/if}
                {/each}
              </ul>
            </Popover.Content>
          </Popover.Root>
        {/if}
        <TagWrapper tags={activityTags} />
      </div>
      <h3 class="text-base font-medium m-0">
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <span class='cursor-pointer hover:underline text-foreground' on:click={(e) => {
          e.stopPropagation();
          e.currentTarget.dispatchEvent(new CustomEvent('editEvent', {
            bubbles: true,
            detail: { date: new Date(activity.start_date), exercise: activity }
          }));
        }}>
          {placeholderName}
        </span>
      </h3>
    </div>
    <span>
      XSS:
      <XSSDisplay
        xss={formattedXSSDetails}
        grayscale={isHidden || isDuplicate}
      />
    </span>
  </div>

  {#if matchedActivities.length > 0 && formattedCompletedXSS && xssDeviationCategory}
    {#key activity.path}
      <div class="inline-grid text-xs font-medium w-full mt-1 max-w-[350px]" style="grid-template-columns: auto repeat(4, auto); gap:2px 12px;">
        <span>Target XSS</span>
        <span class='text-center tabular-nums'>{formattedXSSDetails.xss}</span>
        <span class="text-center tabular-nums text-[var(--color-xlss)]">{formattedXSSDetails.xlss}</span>
        <span class="text-center tabular-nums text-[var(--color-xhss)]">{formattedXSSDetails.xhss}</span>
        <span class="text-center tabular-nums text-[var(--color-xpss)]">{formattedXSSDetails.xpss}</span>

        <!-- Completed Activity Row(s) -->
        {#if matchedActivities.length > 1}
          <div class='col-span-5 border-0 border-solid border-t border-border' />
          {#each matchedActivities as ma}
            {@const formattedXSS = formatXSSDetails({
              xss: ma.xss ?? 0,
              xlss: ma.xlss ?? 0,
              xhss: ma.xhss ?? 0,
              xpss: ma.xpss ?? 0,
            })}
            <span class='whitespace-nowrap text-gray-600 max-w-[120px] truncate opacity-80'>{ma.name}</span>
            <span class='text-center tabular-nums opacity-80'>{formattedXSS.xss}</span>
            <span class="text-center tabular-nums text-[var(--color-xlss)] opacity-80">{formattedXSS.xlss}</span>
            <span class="text-center tabular-nums text-[var(--color-xhss)] opacity-80">{formattedXSS.xhss}</span>
            <span class="text-center tabular-nums text-[var(--color-xpss)] opacity-80">{formattedXSS.xpss}</span>
          {/each}
          <div class='col-span-5 border-0 border-solid border-t border-border' />
        {/if}

        <span>{matchedActivities.length > 1 ? 'Total ' : ''}Completed XSS</span>
        <span class='text-center tabular-nums'>{formattedCompletedXSS?.xss}</span>
        <span class="text-center tabular-nums text-[var(--color-xlss)]">{formattedCompletedXSS?.xlss}</span>
        <span class="text-center tabular-nums text-[var(--color-xhss)]">{formattedCompletedXSS?.xhss}</span>
        <span class="text-center tabular-nums text-[var(--color-xpss)]">{formattedCompletedXSS?.xpss}</span>

        <div />
        <div />
        <i class={cn('fa-solid fa-lg w-full text-center',
          xssDeviationCategory.xlss.suitability === XSSDeviationCategory.Good ? 'text-[var(--color-xss-deviation-good)] fa-badge-check' :
          xssDeviationCategory.xlss.suitability === XSSDeviationCategory.Warn ? 'text-[var(--color-xss-deviation-warn)] fa-circle-exclamation' :
          'text-[var(--color-xss-deviation-alert)] fa-circle-xmark'
        )} />
        <i class={cn('fa-solid fa-lg w-full text-center',
          xssDeviationCategory.xhss.suitability === XSSDeviationCategory.Good ? 'text-[var(--color-xss-deviation-good)] fa-badge-check' :
          xssDeviationCategory.xhss.suitability === XSSDeviationCategory.Warn ? 'text-[var(--color-xss-deviation-warn)] fa-circle-exclamation' :
          'text-[var(--color-xss-deviation-alert)] fa-circle-xmark'
        )} />
        <i class={cn('fa-solid fa-lg w-full text-center',
          xssDeviationCategory.xpss.suitability === XSSDeviationCategory.Good ? 'text-[var(--color-xss-deviation-good)] fa-badge-check' :
          xssDeviationCategory.xpss.suitability === XSSDeviationCategory.Warn ? 'text-[var(--color-xss-deviation-warn)] fa-circle-exclamation' :
          'text-[var(--color-xss-deviation-alert)] fa-circle-xmark'
        )} />
      </div>
    {/key}
  {/if}

  {#if !isPlaceholder}
    <h3 class="text-base font-medium m-0 text-muted-foreground">
      Assigned Training{assignedTrainingDescriptor ? `: ${assignedTrainingDescriptor}` : ''}
    </h3>
    <div class='border-0 border-solid border-l-[3px] border-border pl-2'>
      <TodayActivity hideActions {activity} {todaysActivities} {selectable} {onSelectedChange} {useTimelineLayout} {activityImageCarouselNumber} bind:selected />
    </div>
  {/if}

  <ForecastActivityExplanation className='mt-0' {activity} {forecastInfo} {myFitness} />

  {#if !isPlaceholder}
    <Separator orientation='horizontal' />
  {/if}

  <TodayActivityCardActions {todaysActivities} {activity} bind:disabled={isLoading} transparent={isHidden || isDuplicate} />
</div>
